import React, { useEffect, useState, useRef } from 'react';
import { FileTextIcon, ImageIcon, VideoIcon, FileIcon, CodeIcon, FileSpreadsheetIcon, GlobeIcon, MoreVerticalIcon, DownloadIcon, TrashIcon, BookOpenIcon, YoutubeIcon } from 'lucide-react';
import { DocumentSummaryModal } from './DocumentSummaryModal';
interface Props {
  document: {
    id: string;
    name: string;
    type: string;
    uploadedAt: string;
    size: string;
    summary: string | null;
  };
}
export const DocumentRow = ({
  document
}: Props) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node) && !buttonRef.current?.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    // Only add listeners if menu is shown
    if (showMenu) {
      window.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('keydown', handleEscape);
      return () => {
        window.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showMenu]);
  // Handle escape key
  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowMenu(false);
    }
  };
  const getFileIcon = () => {
    switch (document.type) {
      case 'video':
        return <YoutubeIcon size={20} className="text-red-400" data-id="element-1003" />;
      case 'url':
        return <GlobeIcon size={20} className="text-blue-400" data-id="element-1004" />;
      case 'pdf':
        return <FileTextIcon size={20} className="text-red-400" data-id="element-1005" />;
      case 'image':
      case 'jpg':
      case 'png':
        return <ImageIcon size={20} className="text-blue-400" data-id="element-1006" />;
      case 'video':
      case 'mp4':
        return <VideoIcon size={20} className="text-purple-400" data-id="element-1007" />;
      case 'code':
        return <CodeIcon size={20} className="text-green-400" data-id="element-1008" />;
      case 'xlsx':
        return <FileSpreadsheetIcon size={20} className="text-emerald-400" data-id="element-1009" />;
      case 'pptx':
        return <FileIcon size={20} className="text-orange-400" data-id="element-1010" />;
      default:
        return <FileIcon size={20} className="text-gray-400" data-id="element-1011" />;
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(!showMenu);
  };
  const handleAction = (action: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    switch (action) {
      case 'edit':
        setShowSummaryModal(true);
        break;
      case 'download':
        console.log('Download:', document.id);
        break;
      case 'delete':
        console.log('Delete:', document.id);
        break;
    }
    setShowMenu(false);
  };
  const handleSaveSummary = async (summary: string) => {
    // In a real implementation, this would make an API call
    // For now, we'll just simulate an async operation
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Saving summary:', summary);
  };
  const handleMobileClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(false);
  };
  return <>
      <div className="group px-3 sm:px-6 py-3 sm:py-4 hover:bg-surface-mid transition-colors" data-id="element-1012">
        <div className="flex items-start justify-between gap-2 sm:gap-4" data-id="element-1013">
          <div className="flex items-start gap-2 sm:gap-4 flex-1 min-w-0" data-id="element-1014">
            <div className="p-1.5 sm:p-2 bg-surface rounded-none border border-surface-border shrink-0" data-id="element-1015">
              {getFileIcon()}
            </div>
            <div className="flex-1 min-w-0" data-id="element-1016">
              <h3 className="text-sm font-medium text-white font-mono mb-0.5 sm:mb-1 truncate" data-id="element-1017">
                {document.name}
              </h3>
              {document.summary && <p className="text-sm text-gray-400 line-clamp-2 hidden sm:block" data-id="element-1018">
                  {document.summary}
                </p>}
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-4 mt-1 sm:mt-2 text-xs text-gray-400" data-id="element-1019">
                <span className="whitespace-nowrap" data-id="element-1020">
                  {formatDate(document.uploadedAt)}
                </span>
                <span className="whitespace-nowrap" data-id="element-1021">{document.size}</span>
              </div>
            </div>
          </div>
          {/* Desktop Menu Button */}
          <div className="relative" data-id="element-1022">
            <button ref={buttonRef} onClick={handleMenuClick} className="p-1.5 text-gray-400 hover:text-gray-300 hover:bg-surface rounded-none transition-colors" aria-label="Document actions" aria-haspopup="true" aria-expanded={showMenu} data-id="element-1023">
              <MoreVerticalIcon size={16} data-id="element-1024" />
            </button>
            {/* Desktop Menu */}
            {showMenu && <div ref={menuRef} className="absolute right-0 top-full mt-1 w-56 bg-surface-light border border-surface-border rounded-none shadow-lg z-[100]" role="menu" aria-orientation="vertical" aria-labelledby="document-menu" data-id="element-1025">
                <div className="py-1" data-id="element-1026">
                  <button onClick={e => handleAction('edit', e)} className="w-full px-4 py-2 text-sm text-left text-gray-400 hover:text-white hover:bg-surface flex items-center gap-2" role="menuitem" data-id="element-1027">
                    <BookOpenIcon size={16} data-id="element-1028" />
                    <span data-id="element-1029">View/Edit Summary</span>
                  </button>
                  <button onClick={e => handleAction('download', e)} className="w-full px-4 py-2 text-sm text-left text-gray-400 hover:text-white hover:bg-surface flex items-center gap-2" role="menuitem" data-id="element-1030">
                    <DownloadIcon size={16} data-id="element-1031" />
                    <span data-id="element-1032">Download Document</span>
                  </button>
                  <div className="border-t border-surface-border my-1" data-id="element-1033" />
                  <button onClick={e => handleAction('delete', e)} className="w-full px-4 py-2 text-sm text-left text-status-error hover:bg-surface flex items-center gap-2" role="menuitem" data-id="element-1034">
                    <TrashIcon size={16} data-id="element-1035" />
                    <span data-id="element-1036">Delete Document</span>
                  </button>
                </div>
              </div>}
          </div>
        </div>
        {/* Mobile Menu */}
        {showMenu && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] sm:hidden" onClick={handleMobileClose} data-id="element-1037">
            <div className="absolute bottom-0 left-0 right-0 bg-surface-light border-t border-surface-border animate-in slide-in-from-bottom-2" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Document actions" data-id="element-1038">
              <div className="p-4 space-y-2" data-id="element-1039">
                <button onClick={e => handleAction('edit', e)} className="w-full px-4 py-3 text-sm text-left text-gray-400 hover:text-white hover:bg-surface flex items-center gap-3 rounded-none" data-id="element-1040">
                  <BookOpenIcon size={18} data-id="element-1041" />
                  <span data-id="element-1042">View/Edit Summary</span>
                </button>
                <button onClick={e => handleAction('download', e)} className="w-full px-4 py-3 text-sm text-left text-gray-400 hover:text-white hover:bg-surface flex items-center gap-3 rounded-none" data-id="element-1043">
                  <DownloadIcon size={18} data-id="element-1044" />
                  <span data-id="element-1045">Download Document</span>
                </button>
                <div className="border-t border-surface-border my-2" data-id="element-1046" />
                <button onClick={e => handleAction('delete', e)} className="w-full px-4 py-3 text-sm text-left text-status-error hover:bg-surface flex items-center gap-3 rounded-none" data-id="element-1047">
                  <TrashIcon size={18} data-id="element-1048" />
                  <span data-id="element-1049">Delete Document</span>
                </button>
              </div>
              <button onClick={handleMobileClose} className="w-full border-t border-surface-border p-4 text-sm text-gray-400 hover:text-white hover:bg-surface" data-id="element-1050">
                Cancel
              </button>
            </div>
          </div>}
      </div>
      {/* Add Summary Modal */}
      <DocumentSummaryModal isOpen={showSummaryModal} onClose={() => setShowSummaryModal(false)} document={document} onSave={handleSaveSummary} data-id="element-1051" />
    </>;
};